import React, { useState, useRef, useEffect } from "react";
import Fab from "@mui/material/Fab";
import CircularProgress from "@mui/material/CircularProgress";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import IconButton from "@mui/material/IconButton";
import DeleteForever from "@mui/icons-material/DeleteForever";
import "./VoiceButton.css";

function getEnvVar(key, fallback = "") {
  if (typeof process !== "undefined" && process.env && process.env[key]) {
    return process.env[key];
  }
  else {
    return fallback;
  }
}

// const API_ENDPOINT = "https://n8n.khaivinh.com/webhook/voice-map"; // <-- UPDATE THIS
const OPENAI_BASE_URL = "https://api.openai.com/v1/realtime";
const OPENAI_MODEL = getEnvVar("REACT_APP_OPENAI_MODEL", "gpt-4o-realtime-preview-2024-12-17");
const OPENAI_INSTRUCTIONS = getEnvVar("REACT_APP_OPENAI_INSTRUCTIONS", "You are a friendly assistant to help me explore the map and answer questions, if the question relates to the map navigation (pan, move, zoom, fly, etc.) then answer something same like: OK I will do it now");
const OPENAI_API_KEY = getEnvVar("REACT_APP_OPENAI_API_KEY", "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");

const OPENAI_CONFIG = {
  instructions: OPENAI_INSTRUCTIONS,
  model: OPENAI_MODEL,
  modalities: ["audio", "text"],
  voice: "alloy",
  input_audio_transcription: {
      model: "whisper-1"
  }
};

const VoiceButton = ({ onCommand }) => {
  const [state, setState] = useState("IDLE"); // IDLE, LISTENING, PROCESSING
  const [curMsg, setCurMsg] = useState("");
  const [messages, setMessages] = useState([]); // {role: 'user'|'ai', text: string}
  const [showChat, setShowChat] = useState(false);
  const [chatFade, setChatFade] = useState(""); // "fade-in" | "fade-out" | ""
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioElRef = useRef(null);
  const sessionActiveRef = useRef(false);
  const micStreamRef = useRef(null); // Store the mic stream

  // Toggle session on/off
  const handleToggle = async () => {
    if (state === "IDLE") {
      setState("LISTENING");
      if (!showChat) {
        setShowChat(true);
        setTimeout(() => setChatFade("fade-in"), 10); // trigger fade-in
      }
      await startSession();
    } else {
      stopSession();
      setState("IDLE");
    }
  };

  // Fetch ephemeral key from OpenAI
  const fetchEphemeralKey = async () => {
    try {
      const response = await fetch("https://api.openai.com/v1/realtime/sessions", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${OPENAI_API_KEY}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(OPENAI_CONFIG)
      });
      const data = await response.json();
      if (data && data.client_secret && data.client_secret.value) {
        return data.client_secret.value;
      } else {
        throw new Error("No client_secret.value in OpenAI session response");
      }
    } catch (err) {
      alert("Failed to fetch OpenAI ephemeral key: " + err.message);
      throw err;
    }
  };

  // Start OpenAI realtime session
  const startSession = async () => {
    try {
      setState("PROCESSING");
      const key = await fetchEphemeralKey();
      const pc = new window.RTCPeerConnection();
      pcRef.current = pc;
      sessionActiveRef.current = true;

      // Set up to play remote audio from the model
      let audioEl = audioElRef.current;
      if (typeof document !== "undefined") {
        if (!audioEl) {
          audioEl = document.createElement("audio");
          audioEl.autoplay = true;
          audioElRef.current = audioEl;
          document.body.appendChild(audioEl);
        }
        if (typeof window !== "undefined") {
          pc.ontrack = e => {
            audioEl.srcObject = e.streams[0];
          };
        }
      }

      // Add local audio track for microphone input in the browser
      if (typeof navigator !== "undefined" && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = ms;
        pc.addTrack(ms.getTracks()[0]);
      }

      // Set up data channel for sending and receiving events
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      // When message is received from the data channel, handle it
      dc.addEventListener("message", (e) => {
        handleResponse(e);
      });

      // After creating the data channel send the greeting prompt
      dc.addEventListener("open", () => {
        setState("LISTENING");
        const greetingPrompt = "Hi, how can I help you today?";
        const event = {
          type: "response.create",
          response: {
            input: [],
            instructions: greetingPrompt,
          },
        };
        dc.send(JSON.stringify(event));
      });

      // Start the session using the Session Description Protocol (SDP)
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Send offer with config as query param
      const configParam = encodeURIComponent(JSON.stringify(OPENAI_CONFIG));
      const sdpResponse = await fetch(`${OPENAI_BASE_URL}?model=${OPENAI_MODEL}&config=${configParam}`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${key}`,
          "Content-Type": "application/sdp"
        },
      });

      const answer = {
        type: "answer",
        sdp: await sdpResponse.text(),
      };
      await pc.setRemoteDescription(answer);
    } catch (err) {
      alert("Failed to start OpenAI realtime session: " + err.message);
      stopSession();
      setState("IDLE");
    }
  };

  // Stop session and cleanup
  const stopSession = () => {
    sessionActiveRef.current = false;
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (typeof document !== "undefined" && audioElRef.current) {
      audioElRef.current.srcObject = null;
      document.body.removeChild(audioElRef.current);
      audioElRef.current = null;
    }
    // Stop the microphone stream if active
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    setCurMsg("");
    // Optionally keep or clear messages
  };

  // Handle OpenAI events
  const handleResponse = (e) => {
    let obj;
    try {
      obj = JSON.parse(e.data);
    } catch {
      console.error("Error parsing JSON:", e.data);
      return;
    }
    if (obj.type === 'response.audio_transcript.done') {
      setCurMsg("");
      setMessages(msgs => [...msgs, { role: 'ai', text: obj.transcript }]);
    }
    if (obj.type === 'conversation.item.input_audio_transcription.completed') {
      console.log("obj.transcript", obj.transcript);
      setCurMsg("");
      setMessages(msgs => [...msgs, { role: 'user', text: obj.transcript }]);
      
      // Send user text to webhook
      // sendToWebhook(obj.transcript);

      const prompt = `
          Analyze the conversation so far.
          You are an AI assistant tasked with interpreting user commands for a MapLibre map and responding with a JSON object.
          This JSON object will describe the requested map manipulation.
          Here's the structure I expect for your JSON output without instruction or markdown:

          [
            {
              "command": "move",
              "direction": "left" | "right" | "up" | "down",
              "value": <number_of_distance default 200>
            },
            {
              "command": "clear_markers"
            },
            {
              "command": "zoom",
              "action": "in" | "out" | "set",
              "level": <number_for_set_action_only>
            },
            {
              "command": "pan_to",
              "latitude": <number_latitude>,
              "longitude": <number_longitude>,
              "zoom": <number_zoom_level_optional default 15>
            },
            {
              "command": "query",
              "type": "center" | "zoom"
            }
          ]
          `;

          const event = {
            type: "response.create",
            response: {
              // Setting to "none" indicates the response is out of band
              // and will not be added to the default conversation
              conversation: "none",

              // Set metadata to help identify responses sent back from the model
              metadata: { topic: "map_control" },
              
              // Set any other available response fields
              modalities: [ "text" ],
              instructions: prompt,
            },
          };

      // WebRTC data channel and WebSocket both have .send()
      dcRef.current.send(JSON.stringify(event));
    }
    if (obj.type === 'response.audio_transcript.delta') {
      setCurMsg(cur => cur + (obj.delta || ""));
    }
    if (obj.type === 'conversation.item.input_audio_transcription.delta') {
      setCurMsg(cur => cur + (obj.delta || ""));
    }
    // Handle out-of-band map control response.done
    if (obj.type === 'response.done' && obj.response?.metadata?.topic === 'map_control') {
      console.log("obj.response", obj.response);
      // this server event pertained to our OOB map control response
      if (typeof onCommand === 'function') {
        // get and parse the JSON object from the response obj.response.output[0].content[0].text, try catch
        try {
          // check if the data in output[0].content[0].text is there and text is a valid JSON object

          if (obj.response.output.length > 0 && obj.response.output[0].content.length > 0) {
            // if text is start is markdown json block, remove the markdown block
            let text = obj.response.output[0].content[0].text;
            if (text.startsWith("```json")) {
              text = text.substring(7);
            }
            if (text.endsWith("```")) {
              text = text.substring(0, text.length - 1);
            }
            const json = JSON.parse(text);
            onCommand(json);
          }
        } catch (err) {
          console.error("Error parsing JSON:", obj.response.output[0].content[0].text);
        }
      }
    }
  };

  // Clear chat history and hide chat with fade-out
  const handleClear = () => {
    if (state === "IDLE") {
      setChatFade("fade-out");
      setTimeout(() => {
        setShowChat(false);
        setMessages([]);
        setCurMsg("");
        setChatFade("");
        onCommand({"command": "clear_markers"});
      }, 350); // match CSS transition duration
    } else {
      setMessages([]);
      setCurMsg("");
    }
  };

  // Add scroll-to-bottom effect
  const messagesEndRef = useRef(null);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, curMsg]);

  // Button icon and style
  let icon, color, animate;
  if (state === "IDLE") {
    icon = <MicIcon />;
    color = "primary";
    animate = false;
  } else if (state === "LISTENING") {
    icon = <StopIcon />;
    color = "secondary";
    animate = true;
  } else if (state === "PROCESSING") {
    icon = <CircularProgress size={24} color="inherit" />;
    color = "default";
    animate = false;
  }

  return (
    <div className={`voice-fab-container${animate ? " pulse" : ""}`}>
      <Fab
        color={color}
        aria-label="voice"
        onClick={handleToggle}
        disabled={state === "PROCESSING"}
      >
        {icon}
      </Fab>
      {showChat && (
        <div className={`voice-messages ${chatFade}`}>
          <div className="voice-messages-header" style={{ justifyContent: 'flex-end' }}>
            <span style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>Please ask anything, I will help you to answer</span>
            <IconButton size="small" onClick={handleClear} aria-label="clear chat">
              <DeleteForever fontSize="small" />
            </IconButton>
          </div>
          <div className="voice-messages-list">
            {messages.map((msg, i) => (
              <div key={i} className={msg.role === 'user' ? 'msgUsr' : 'msg'}>
                {msg.text}
              </div>
            ))}
            {curMsg && (
              <div className="msgUsr">{curMsg}</div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceButton; 